
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { EmptyState } from "@/components/assessment-history/EmptyState";
import { AssessmentList } from "@/components/assessment-history/AssessmentList";
import { useAssessmentHistory } from "@/hooks/use-assessment-history";
import { useToast } from "@/hooks/use-toast";
import { findOrCreateProfile } from "@/services/userProfileService";

const AssessmentHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: assessments, isLoading, isError, error } = useAssessmentHistory(user?.email);

  const breadcrumbs = [
    { title: "Home", href: "/" },
    { title: "Assessment History", href: "/assessment-history" },
  ];

  // Function to ensure user profile exists
  const ensureUserProfileExists = async () => {
    if (!user?.email) return;
    
    try {
      // This will create the profile if it doesn't exist
      await findOrCreateProfile(user.email, user.fullName || "");
      // Refresh the page to reload data
      window.location.reload();
    } catch (err) {
      console.error("Error ensuring user profile exists:", err);
      toast({
        title: "Error",
        description: "Could not create user profile",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Enhanced error UI with retry and profile creation functionality
  if (isError) {
    const errorMessage = error instanceof Error 
      ? error.message 
      : "There was a problem loading your assessment history";
    
    const isProfileError = errorMessage.includes("user profile");

    return (
      <div className="min-h-screen">
        <PageHeader
          title="Assessment History"
          description="View and manage your past assessments"
          breadcrumbs={breadcrumbs}
        />
        <div className="container mx-auto py-8">
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Assessments</h2>
            <p className="text-gray-500 text-center mb-6">
              {errorMessage}
            </p>
            {isProfileError && (
              <Button 
                onClick={ensureUserProfileExists} 
                className="mb-4"
              >
                Create Profile
              </Button>
            )}
            <Button 
              onClick={() => navigate("/")} 
              variant="outline" 
              className="mr-2"
            >
              Go Home
            </Button>
            <Button 
              onClick={() => window.location.reload()} 
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Assessment History"
        description="View and manage your past assessments"
        breadcrumbs={breadcrumbs}
      />
      <div className="container mx-auto py-8">
        {!assessments || assessments.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between">
              <Button onClick={() => navigate("/assessment")}>Take New Assessment</Button>
            </div>
            <AssessmentList assessments={assessments} />
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AssessmentHistory;
